
import { useState } from 'react';
import { Frame, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { accountSwitchClass } from '@/components/painel/account-settings/account-settings-shared';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { painelButtonOutlineClass, painelSelectContentClass } from '@/components/painel/painel-styles';
import { Button } from '@/components/ui/button';
import {
  aceFrameImageSrc,
  emptyAvatarFrame,
  type AceAvatarFrame,
} from '@/lib/ace-avatar-frames';
import { uploadAvatarFrameImage } from '@/lib/avatar-frame-upload';
import { cn } from '@/lib/utils';

export type AvatarFramesEditorValue = {
  enabled: boolean;
  frames: AceAvatarFrame[];
};

type AvatarFramesEditorProps = {
  value: AvatarFramesEditorValue;
  onChange: (value: AvatarFramesEditorValue) => void;
  description?: string;
};

function FramePreviewCard({ frame }: { frame: AceAvatarFrame }) {
  const [failed, setFailed] = useState(false);
  const src = aceFrameImageSrc(frame);
  const hasImage = Boolean(frame.imageUrl?.trim()) || !failed;

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.1] bg-white/[0.04]">
      <div className="relative flex aspect-square items-center justify-center bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-3">
        {hasImage ? (
          <img
            src={src}
            alt={frame.name}
            className={cn(
              'max-h-full max-w-full object-contain',
              frame.shape === 'round' && 'rounded-full'
            )}
            onError={() => setFailed(true)}
          />
        ) : (
          <div
            className={cn(
              'flex h-20 w-20 items-center justify-center border-2 border-dashed border-amber-400/40 bg-amber-500/10',
              frame.shape === 'round' ? 'rounded-full' : 'rounded-xl'
            )}
          >
            <Frame className="h-6 w-6 text-amber-300/70" />
          </div>
        )}
        {frame.shape === 'round' ? (
          <span className="absolute right-1.5 top-1.5 rounded-full bg-violet-500/20 px-1.5 py-0.5 font-poppins text-[9px] font-semibold text-violet-200">
            Redonda
          </span>
        ) : null}
      </div>
      <p className="border-t border-white/[0.08] px-2 py-2 text-center font-poppins text-xs font-semibold text-slate-200">
        {frame.name || 'Sem nome'}
      </p>
    </div>
  );
}

function FrameEditCard({
  frame,
  index,
  onChange,
  onRemove,
}: {
  frame: AceAvatarFrame;
  index: number;
  onChange: (frame: AceAvatarFrame) => void;
  onRemove: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    const frameId = frame.id.trim().toLowerCase();
    if (frameId.length < 2) {
      setUploadError('Defina um ID (slug) válido antes de enviar a imagem.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const url = await uploadAvatarFrameImage(frameId, file);
      onChange({ ...frame, imageUrl: url });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Falha no envio.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4 lg:grid-cols-[140px_minmax(0,1fr)_auto]">
      <FramePreviewCard frame={frame} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5 sm:col-span-2">
          <Label className="text-xs text-blue-100/80">Nome exibido</Label>
          <Input
            value={frame.name}
            onChange={(e) => onChange({ ...frame, name: e.target.value })}
            className={painelInputClass}
            placeholder="HeartBloom"
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs text-blue-100/80">ID (slug)</Label>
          <Input
            value={frame.id}
            onChange={(e) => onChange({ ...frame, id: e.target.value.toLowerCase() })}
            className={cn(painelInputClass, 'font-mono text-xs')}
            placeholder="heartbloom"
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs text-blue-100/80">Forma</Label>
          <Select
            value={frame.shape ?? 'square'}
            onValueChange={(v) => onChange({ ...frame, shape: v as 'square' | 'round' })}
          >
            <SelectTrigger className={cn(painelInputClass, 'h-10')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={painelSelectContentClass}>
              <SelectItem value="square" className="focus:bg-white/10 focus:text-white">
                Quadrada
              </SelectItem>
              <SelectItem value="round" className="focus:bg-white/10 focus:text-white">
                Redonda
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2 sm:col-span-2">
          <Label className="text-xs text-blue-100/80">Imagem da moldura</Label>
          <div className="flex flex-wrap items-center gap-2">
            <label
              className={cn(
                painelButtonOutlineClass,
                'inline-flex h-10 cursor-pointer items-center gap-2 px-4 font-inter text-sm',
                uploading && 'pointer-events-none opacity-60'
              )}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {frame.imageUrl ? 'Substituir imagem' : 'Enviar imagem'}
              <input
                type="file"
                accept="image/png,image/webp,image/gif"
                className="sr-only"
                disabled={uploading}
                onChange={(e) => {
                  void handleUpload(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
            </label>
            {frame.imageUrl ? (
              <span className="truncate font-mono text-[11px] text-slate-500">{frame.imageUrl}</span>
            ) : (
              <span className="font-inter text-xs text-slate-500">PNG, WebP ou GIF — máx. 5 MB</span>
            )}
          </div>
          {uploadError ? (
            <p className="font-inter text-xs text-rose-300">{uploadError}</p>
          ) : null}
        </div>
      </div>
      <div className="flex items-start justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0 text-rose-300 hover:bg-rose-500/15"
          onClick={onRemove}
          aria-label={`Remover moldura ${index + 1}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function AvatarFramesEditor({ value, onChange, description }: AvatarFramesEditorProps) {
  const patch = (partial: Partial<AvatarFramesEditorValue>) => {
    onChange({ ...value, ...partial });
  };

  const updateFrame = (index: number, frame: AceAvatarFrame) => {
    const frames = [...value.frames];
    frames[index] = frame;
    patch({ frames });
  };

  const removeFrame = (index: number) => {
    patch({ frames: value.frames.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3">
        <Switch
          id="avatar-frames-enabled"
          checked={value.enabled}
          onCheckedChange={(enabled) => patch({ enabled })}
          className={cn(accountSwitchClass, 'mt-0.5 shrink-0')}
        />
        <div className="min-w-0">
          <Label htmlFor="avatar-frames-enabled" className="font-poppins text-sm font-medium text-white">
            Publicar secção Avatar frames
          </Label>
          <p className="mt-1 font-inter text-xs text-slate-400">
            {description ??
              'Exibe a galeria de molduras na página pública, como no Ace Vaf.'}
          </p>
        </div>
      </div>

      {value.enabled ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {value.frames.map((frame) => (
              <FramePreviewCard key={frame.id} frame={frame} />
            ))}
            <button
              type="button"
              onClick={() => patch({ frames: [...value.frames, emptyAvatarFrame()] })}
              className={cn(
                'flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed',
                'border-white/15 bg-white/[0.02] text-slate-400 transition-colors',
                'hover:border-sky-400/35 hover:bg-sky-500/10 hover:text-sky-200'
              )}
            >
              <Plus className="h-8 w-8" />
              <span className="font-poppins text-xs font-semibold">Adicionar moldura</span>
            </button>
          </div>

          <div className="space-y-3">
            <p className="font-poppins text-sm font-medium text-slate-300">Editar molduras</p>
            {value.frames.length === 0 ? (
              <p className="rounded-xl border border-dashed border-white/10 px-4 py-6 text-center font-inter text-sm text-slate-500">
                Nenhuma moldura ainda. Clique no cartão «+» acima para adicionar a primeira.
              </p>
            ) : (
              value.frames.map((frame, index) => (
                <FrameEditCard
                  key={`${frame.id}-${index}`}
                  frame={frame}
                  index={index}
                  onChange={(next) => updateFrame(index, next)}
                  onRemove={() => removeFrame(index)}
                />
              ))
            )}
          </div>
        </>
      ) : (
        <p className="font-inter text-sm text-slate-500">
          Secção desligada — não aparece na página pública. Ative para configurar molduras de avatar.
        </p>
      )}
    </div>
  );
}
